import type { ContentCardData } from "../../../@types/portalAluno";
import type {
  HomeApiItem,
  PortalContentKind,
} from "../../../@types/portalAluno";
import { asBoolean, asId, asNumber, asText } from "../coerce";
import { firstDurationLabel } from "../duration";

const CONTENT_KINDS_WITH_MODULES = new Set<PortalContentKind>([
  "comunicados",
  "cursos",
  "seguranca-do-trabalho",
  "treinamentos-de-1-ponto",
  "trilhas",
]);

const itemFields: Record<
  PortalContentKind,
  {
    date: keyof HomeApiItem;
    id: keyof HomeApiItem;
    image: keyof HomeApiItem;
    title: keyof HomeApiItem;
  }
> = {
  conteudos: {
    date: "dtConteudoInicio",
    id: "idConteudo",
    image: "imgConteudo",
    title: "nmConteudo",
  },
  comunicados: {
    date: "dtCursoComunicadoInicio",
    id: "idCursoComunicado",
    image: "imgCursoComunicado",
    title: "nmCursoComunicado",
  },
  cursos: {
    date: "dtCursoInicio",
    id: "idCurso",
    image: "imgCurso",
    title: "nmCurso",
  },
  destaques: {
    date: "dtDestaqueInicio",
    id: "idConteudo",
    image: "urlCapaDestaque",
    title: "nmConteudo",
  },
  "seguranca-do-trabalho": {
    date: "dtSegurancaNoTrabalhoInicio",
    id: "idSegurancaNoTrabalho",
    image: "imgSegurancaNoTrabalho",
    title: "nmSegurancaNoTrabalho",
  },
  "treinamentos-de-1-ponto": {
    date: "dtCursoT1Inicio",
    id: "idCursoT1",
    image: "imgCursoT1",
    title: "nmCursoT1",
  },
  trilhas: {
    date: "dtTrilhaInicio",
    id: "idTrilha",
    image: "imgTrilha",
    title: "nmTrilha",
  },
  "biblioteca-multimidia": {
    date: "dtConteudoInicio",
    id: "idConteudo",
    image: "imgConteudo",
    title: "nmConteudo",
  },
  "biblioteca-documentos": {
    date: "dtConteudoInicio",
    id: "idConteudo",
    image: "imgConteudo",
    title: "nmConteudo",
  },
  "biblioteca-games": {
    date: "dtConteudoInicio",
    id: "idConteudo",
    image: "imgConteudo",
    title: "nmConteudo",
  },
};

const DATE_KEYS: Array<keyof HomeApiItem> = [
  "dtDestaqueInicio",
  "dtConteudoInicio",
  "dtCursoInicio",
  "dtTrilhaInicio",
  "dtCursoT1Inicio",
  "dtCursoComunicadoInicio",
  "dtSegurancaNoTrabalhoInicio",
];

const asIsoDate = (value: unknown) => {
  if (typeof value === "number") {
    const fromEpoch = new Date(value);
    return Number.isNaN(fromEpoch.getTime())
      ? undefined
      : fromEpoch.toISOString();
  }

  const text = asText(value);

  if (!text) return undefined;

  return Number.isNaN(new Date(text).getTime()) ? undefined : text;
};

const readPublishedAt = (item: HomeApiItem, dateKey: keyof HomeApiItem) => {
  const preferred = asIsoDate(item[dateKey]);

  if (preferred) return preferred;

  for (const key of DATE_KEYS) {
    const fallback = asIsoDate(item[key]);

    if (fallback) return fallback;
  }

  return undefined;
};

export const asItemArray = (value: unknown): HomeApiItem[] =>
  Array.isArray(value)
    ? value.filter(
        (item): item is HomeApiItem =>
          Boolean(item) && typeof item === "object" && !Array.isArray(item)
      )
    : [];

const asNonNegativeNumber = (value: unknown) => {
  const number = asNumber(value);
  return number !== undefined && number >= 0 ? number : undefined;
};

const asPositiveInteger = (value: unknown) => {
  const number = asNonNegativeNumber(value);
  return number !== undefined && Number.isInteger(number) && number > 0
    ? number
    : undefined;
};

const firstNumber = (...values: unknown[]) => {
  for (const value of values) {
    const number = asNonNegativeNumber(value);
    if (number !== undefined) return number;
  }

  return undefined;
};

const firstInteger = (...values: unknown[]) => {
  for (const value of values) {
    const number = asPositiveInteger(value);
    if (number !== undefined) return number;
  }

  return undefined;
};

const getModulesFromChildren = (item: HomeApiItem, kind: PortalContentKind) => {
  const childrenByKind: Partial<Record<PortalContentKind, unknown>> = {
    comunicados: item.cursoComunicadoConteudos,
    cursos: item.cursoConteudos,
    "seguranca-do-trabalho": item.segurancaNoTrabalhoConteudos,
    "treinamentos-de-1-ponto": item.cursoT1Conteudos,
    trilhas: item.trilhaCursos,
  };
  const children = childrenByKind[kind];

  return Array.isArray(children) && children.length > 0
    ? children.length
    : undefined;
};

export const mapApiItemToCard = (
  item: HomeApiItem,
  kind: PortalContentKind
): ContentCardData | undefined => {
  const fields = itemFields[kind];
  const id = asId(item[fields.id]);
  const title = asText(item[fields.title]);

  if (!id || !title) return undefined;

  return {
    completed: asBoolean(item.isRealizado),
    duration: firstDurationLabel(
      item.tempoTotal,
      item.dsTempoEstimado,
      item.duration
    ),
    id,
    imageAlt: `Capa de ${title}`,
    imageUrl: asText(item[fields.image]) ?? asText(item.imgConteudo),
    publishedAtIso: readPublishedAt(item, fields.date),
    modules: CONTENT_KINDS_WITH_MODULES.has(kind)
      ? firstInteger(
          item.totalModulos,
          item.qtdModulos,
          item.quantidadeModulos,
          item.qtdConteudos,
          item.modules
        ) ?? getModulesFromChildren(item, kind)
      : undefined,
    rating: firstNumber(
      item.rating,
      item.mediaAvaliacao,
      item.avaliacaoMedia,
      item.nota
    ),
    sourceType: kind,
    spriteColumn: (id % 3) as 0 | 1 | 2,
    spriteRow: (Math.floor(id / 3) % 3) as 0 | 1 | 2,
    title,
    unavailable: asBoolean(item.isExpirado),
  };
};

export const mapApiItemsToCards = (
  value: unknown,
  kind: PortalContentKind
) => {
  const seenIds = new Set<number>();

  return asItemArray(value).reduce<ContentCardData[]>((cards, item) => {
    const card = mapApiItemToCard(item, kind);

    if (!card || seenIds.has(card.id)) return cards;

    seenIds.add(card.id);
    cards.push(card);
    return cards;
  }, []);
};

export const onlyCarouselItems = (value: unknown) =>
  asItemArray(value).filter(
    (item) =>
      item.icExibirCarrossel === undefined ||
      Number(item.icExibirCarrossel) !== 0
  );
